/**
 * Delivery Inspector (Phase 6 Action 51).
 *
 * Operator-facing page that resolves a report_deliveries UUID and shows
 * everything needed to answer "why didn't this arrive?" or "what did
 * we send?". Reached via URL (bookmark, log paste, or deep-link from
 * the Health tab's last-failure card).
 *
 * Layout:
 *   • Header: back-to-tenant crumb, delivery status pill, timestamps
 *   • Recipient card: email + name + slack id
 *   • Error card: only when status='failed', renders error_message
 *     verbatim in a monospace block
 *   • Content card: contentPreview (first ~500 chars) with a
 *     "Show full text" toggle for the whole formatted_text
 *   • Related events card: sibling events matched on user/type/year/week
 *
 * PII: this page renders the recipient's email + name + Slack ID and
 * the full delivered summary text (customer names + report notes).
 * See [[mc-pii-posture]] — retrofit path is 1-2 days when compliance
 * forces it. Fields tagged in the store row types.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useParams } from 'react-router-dom';
import { apiPath } from '../api/baseUrl';
import { getStoredToken } from '../auth/AuthContext';

interface DeliveryDetail {
  delivery: {
    id: string;
    userId: string;
    reportType: 'product' | 'customer';
    year: number;
    week: number;
    status: 'generated' | 'delivered' | 'failed';
    formattedText: string | null;
    contentPreview: string;
    errorMessage: string | null;
    deliveredAt: string | null;
    createdAt: string;
  };
  recipient: {
    id: string;
    email: string | null;
    name: string | null;
    slackId: string | null;
  };
  org: {
    id: string;
    name: string;
    slug: string;
  };
  events: Array<{
    eventId: string;
    eventType: string;
    occurredAt: string;
    actorType: string;
    actorId: string | null;
    correlationId: string | null;
    payload: Record<string, unknown>;
  }>;
}

const STATUS_BADGE: Record<DeliveryDetail['delivery']['status'], string> = {
  generated: 'bg-amber-100 text-amber-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  failed:    'bg-red-100 text-red-800',
};

async function fetchJson<T>(url: string): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(apiPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `${url} → ${res.status}`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b.message) msg = b.message;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// Server-side rate limit is 10/hr/operator/tenant; keep the FE copy in
// sync so the "Retry" modal can hint at it before the operator submits.
const REDELIVERY_HOURLY_LIMIT = 10;

interface RedeliverError {
  error: string;
  message?: string;
  retryAfter?: string;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(apiPath(url), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    // Surface the server's error code so the mutation onError can
    // branch — rate_limited needs a different UI than slack_failed.
    const err: RedeliverError & { status: number } = {
      status: res.status,
      error: (json.error as string) ?? 'unknown_error',
      message: (json.message as string) ?? `HTTP ${res.status}`,
      retryAfter: (json.retryAfter as string) ?? undefined,
    };
    throw err;
  }
  return json as T;
}

export default function DeliveryInspector() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['inspector-delivery', id],
    queryFn: () => fetchJson<{ data: DeliveryDetail }>(`/api/inspector/deliveries/${id}`),
    enabled: Boolean(id),
    retry: false, // 404s shouldn't retry
  });

  const [retryOpen, setRetryOpen] = useState(false);
  const [reason, setReason] = useState('');

  const retryMutation = useMutation({
    mutationFn: (r: string) =>
      postJson<{ data: { delivery: { id: string; status: string; deliveredAt: string }; slackTs: string | null } }>(
        `/api/inspector/deliveries/${id}/redeliver`,
        { reason: r },
      ),
    onSuccess: () => {
      // Refetch inspector so status pill + timeline + events update.
      queryClient.invalidateQueries({ queryKey: ['inspector-delivery', id] });
      // Also invalidate the source tenant's health + deliveries lists so
      // Fleet-side rollups stay coherent when the operator navigates back.
      queryClient.invalidateQueries({ queryKey: ['tenant-health'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-deliveries'] });
      setRetryOpen(false);
      setReason('');
    },
  });

  function openRetry() {
    setReason('');
    retryMutation.reset();
    setRetryOpen(true);
  }
  function closeRetry() {
    if (retryMutation.isPending) return; // don't close mid-flight
    setRetryOpen(false);
  }
  function submitRetry() {
    if (reason.trim().length < 10) return;
    retryMutation.mutate(reason.trim());
  }

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading delivery…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load delivery: {q.error instanceof Error ? q.error.message : 'unknown error'}
        <div className="mt-2">
          <Link to="/fleet" className="text-red-700 underline">Back to Fleet</Link>
        </div>
      </div>
    );
  }

  const { delivery, recipient, org, events } = q.data.data;

  return (
    <div>
      <div className="mb-6">
        <div className="text-sm text-slate-500">
          <Link to="/fleet" className="hover:text-slate-700">Fleet</Link>
          <span className="mx-1">›</span>
          <Link to={`/fleet/${org.id}`} className="hover:text-slate-700">{org.name}</Link>
          <span className="mx-1">›</span>
          <span className="text-slate-400">Delivery inspector</span>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {delivery.reportType} · {delivery.year} week {delivery.week}
            </h1>
            <p className="mt-1 text-xs text-slate-500 font-mono">{delivery.id}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Retry only for failed deliveries (Action 52 v1). Delivered
                rows stay read-only until we have a real "customer says
                they didn't get it" use case. */}
            {delivery.status === 'failed' && (
              <button
                type="button"
                onClick={openRetry}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                Retry delivery
              </button>
            )}
            <span className={clsx('inline-flex rounded-full px-3 py-1 text-sm font-medium', STATUS_BADGE[delivery.status])}>
              {delivery.status}
            </span>
          </div>
        </div>
      </div>

      {retryMutation.isSuccess && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          Redelivery succeeded. The recipient has been DM'd the report.
        </div>
      )}

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Recipient</h2>
        </header>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-6 py-4 text-sm">
          <div>
            <dt className="text-slate-500">Email</dt>
            <dd className="mt-0.5 text-slate-900">{recipient.email ?? <span className="text-slate-400">—</span>}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Name</dt>
            <dd className="mt-0.5 text-slate-900">{recipient.name ?? <span className="text-slate-400">—</span>}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Slack ID</dt>
            <dd className="mt-0.5 text-slate-900 font-mono">{recipient.slackId ?? <span className="text-slate-400">—</span>}</dd>
          </div>
          <div>
            <dt className="text-slate-500">User ID</dt>
            <dd className="mt-0.5 text-slate-900 font-mono text-xs">{recipient.id}</dd>
          </div>
        </dl>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Timeline</h2>
        </header>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-6 py-4 text-sm">
          <div>
            <dt className="text-slate-500">Created</dt>
            <dd className="mt-0.5 text-slate-900">{formatDateTime(delivery.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Delivered</dt>
            <dd className="mt-0.5 text-slate-900">{formatDateTime(delivery.deliveredAt)}</dd>
          </div>
        </dl>
      </section>

      {delivery.status === 'failed' && (
        <section className="mb-6 rounded-md border border-red-200 bg-red-50">
          <header className="border-b border-red-200 px-6 py-3">
            <h2 className="text-sm font-semibold text-red-900">Error</h2>
          </header>
          <div className="px-6 py-4 text-sm">
            {delivery.errorMessage ? (
              <pre className="whitespace-pre-wrap break-all font-mono text-red-900">{delivery.errorMessage}</pre>
            ) : (
              <span className="text-red-800">Delivery failed but no error message was recorded.</span>
            )}
          </div>
        </section>
      )}

      <ContentCard formattedText={delivery.formattedText} preview={delivery.contentPreview} />

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Related events</h2>
          <span className="text-xs text-slate-500">
            {events.length} matched by user + type + week
          </span>
        </header>
        {events.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-500">
            No events matched. If the delivery is very recent, the emit may still be in flight; refresh in a moment.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map((e) => <EventRow key={e.eventId} event={e} />)}
          </ul>
        )}
      </section>

      {retryOpen && (
        <RetryModal
          reason={reason}
          setReason={setReason}
          onCancel={closeRetry}
          onSubmit={submitRetry}
          isPending={retryMutation.isPending}
          error={retryMutation.error as (RedeliverError & { status?: number }) | null}
        />
      )}
    </div>
  );
}

function RetryModal({
  reason,
  setReason,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  reason: string;
  setReason: (r: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isPending: boolean;
  error: (RedeliverError & { status?: number }) | null;
}) {
  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= 10 && !isPending;
  const errorCopy = errorMessageFor(error);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-md bg-white shadow-lg">
        <header className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Retry delivery</h2>
          <p className="mt-1 text-xs text-slate-500">
            Re-sends the stored report content to the same recipient. Rate limit: {REDELIVERY_HOURLY_LIMIT}/hour per tenant.
          </p>
        </header>
        <div className="px-6 py-4">
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
            Reason (audit trail)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g., Customer reported non-receipt via support ticket #123; retrying manually."
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            disabled={isPending}
          />
          <div className="mt-1 flex items-center justify-between text-xs">
            <span className={clsx(trimmedLen < 10 ? 'text-slate-400' : 'text-slate-500')}>
              {trimmedLen < 10 ? `${10 - trimmedLen} more character${10 - trimmedLen === 1 ? '' : 's'} required` : 'Ready to submit'}
            </span>
            <span className={clsx('text-slate-400 tabular-nums', trimmedLen > 500 && 'text-red-600')}>
              {trimmedLen} / 500
            </span>
          </div>
          {errorCopy && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {errorCopy}
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {isPending ? 'Sending…' : 'Retry delivery'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// Translates the server's error codes into operator-friendly copy. Kept
// outside the modal so the mapping is trivially unit-testable if we
// ever want to.
function errorMessageFor(err: (RedeliverError & { status?: number }) | null): string | null {
  if (!err) return null;
  switch (err.error) {
    case 'rate_limited': {
      const when = err.retryAfter ? new Date(err.retryAfter) : null;
      const clock = when && !Number.isNaN(when.getTime())
        ? when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        : 'in about an hour';
      return `You've hit the redelivery limit for this tenant (${REDELIVERY_HOURLY_LIMIT}/hour). Try again after ${clock}.`;
    }
    case 'slack_failed':
      // Server's `message` already includes "Slack API rejected the
      // message: <code>", so we don't prepend anything — just append
      // the "still failed" clarification.
      return `${err.message ?? 'Slack API rejected the message.'} The delivery is still marked failed.`;
    case 'no_content_to_send':
      return 'This delivery has no rendered content — nothing to re-send. Trigger the weekly report cron to regenerate.';
    case 'no_slack_connection':
      return 'This tenant is not connected to Slack. Configure Slack from the Config tab first.';
    case 'no_recipient_slack_id':
      return "The recipient user has no slack_id. We can't DM them.";
    case 'not_failed':
      return 'This delivery is not in a failed state.';
    case 'not_found':
      return 'This delivery no longer exists.';
    case 'slack_not_configured':
      return 'Slack is not configured on this Mission Control deployment.';
    default:
      return err.message ?? 'Redelivery failed. Try again.';
  }
}

function ContentCard({ formattedText, preview }: { formattedText: string | null; preview: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = !!formattedText && formattedText.length > preview.length;
  return (
    <section className="mb-6 rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Content sent</h2>
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            {expanded ? 'Show preview only' : 'Show full text'}
          </button>
        )}
      </header>
      <div className="px-6 py-4 text-sm">
        {!formattedText ? (
          <span className="text-slate-500">
            No formatted text stored for this delivery — the generator likely failed before rendering.
          </span>
        ) : (
          <pre className="whitespace-pre-wrap font-mono text-slate-800 text-xs leading-relaxed">
            {expanded ? formattedText : preview}
          </pre>
        )}
      </div>
    </section>
  );
}

function EventRow({ event }: { event: DeliveryDetail['events'][number] }) {
  const [open, setOpen] = useState(false);
  const hasPayload = event.payload && Object.keys(event.payload).length > 0;
  return (
    <li className="px-6 py-3 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-slate-900">{event.eventType}</span>
        <span className="text-xs text-slate-500">by {event.actorType}</span>
        {event.correlationId && (
          <span className="text-xs text-slate-400 font-mono">corr {event.correlationId.slice(0, 8)}</span>
        )}
        <span className="ml-auto text-xs text-slate-500">{formatDateTime(event.occurredAt)}</span>
      </div>
      {hasPayload && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            {open ? 'Hide payload' : 'Show payload'}
          </button>
          {open && (
            <pre className="mt-1 overflow-x-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700 font-mono">
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
